import React from 'react';
import classNames from 'classnames';
import { ICONS } from 'src/common/constants';
import IcoMoon from '../IcoMoon';
import { convertLocalTime } from 'src/helpers/validationHelper';
import FontAwesomeIcon from '../Icon';
import Loader from 'src/components/Loader';
import './Timeline.scss';

interface Actor {
  firstname: string;
  lastname: string;
}

interface ActivityEvent {
  statusName: string;
  timestamp?: string | number;
  actor: Actor;
  action: string;
  reasonType?: string;
  reason?: string;
}

interface TimelineProps {
  activity: ActivityEvent[];
  loading: boolean;
}

const decodeUnicodeEscapeSequences = (input: string): string => {
  const replacedUnicode = input.replace(
    /(\\)?\\u(\w{4})/g,
    (match, escaped, p1) => {
      if (escaped) {
        return match;
      }

      return String.fromCharCode(parseInt(p1, 16));
    }
  );

  return replacedUnicode.replace(/\\n/g, '\n');
};

interface TimelineIconProps {
  statusName: string;
}

const TimelineIcon = ({ statusName }: TimelineIconProps) => {
  return (
    <div className="timeline__icon icomoonHourglass">
      {statusName === 'Under Review' ? (
        <FontAwesomeIcon glyph="hourglass-end" weight="light" />
      ) : statusName === 'One Year Warranty' ? (
        <FontAwesomeIcon glyph="certificate" weight="solid" size="solid" />
      ) : (
        <IcoMoon glyph={ICONS[statusName]} />
      )}
    </div>
  );
};

interface DeclinedReasonProps {
  event: ActivityEvent;
}

const DeclinedReason = ({ event }: DeclinedReasonProps) => {
  if (event.statusName !== 'Declined') {
    return null;
  }

  return (
    <>
      <div className="timeline__log">Reason Type: {event.reasonType}</div>
      <div className="timeline__log">
        Reason Description:{' '}
        {event.reason ? decodeUnicodeEscapeSequences(event.reason) : ''}
      </div>
    </>
  );
};

const Timeline = ({ activity, loading }: TimelineProps) => {
  const renderContent = () => {
    if (loading) {
      return <Loader />;
    }

    if (!activity || activity.length === 0) {
      return (
        <div
          className="timeline__title"
          style={{ fontSize: 10, color: 'grey' }}
        >
          No activity exists for this Service Request yet.
        </div>
      );
    }

    return (
      <ul className="timeline__items">
        {activity.map((event: ActivityEvent, index: number) => (
          <li
            key={index}
            className={classNames(
              'timeline__item',
              `timeline__item--${index === 0 ? 'present' : 'past'}`
            )}
          >
            <div className="timeline__bar" />
            <div className="timeline__details">
              <span className="timeline__type">{event.statusName}</span>{' '}
              {event.timestamp ? convertLocalTime(event.timestamp) : null}
            </div>
            <div className="timeline__log">
              {event.actor.firstname} {event.actor.lastname}
            </div>
            <div className="timeline__log">{event.action}</div>
            <DeclinedReason event={event} />
            <TimelineIcon statusName={event.statusName} />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="timeline">
      <div className="timeline__title">Activity</div>
      {renderContent()}
    </div>
  );
};

export default Timeline;
